import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { PeopleTab } from "client/openapi/models/PeopleTab";
import { PersonKind } from "client/openapi/models/PersonKind";
import type { PersonRow } from "client/openapi/models/PersonRow";
import { PeopleService } from "client/openapi/services/PeopleService";
import {
  KindChip,
  personDetail,
  personKey,
  personName,
  profilePath,
} from "components/Dashboard/PeoplePanel/person";
import {
  KeyboardEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { RECENT_LABEL, RECENT_SHOWN, useRecentPeople } from "util/recentPeople";
import "./index.css";

/** The people found for a search, or null if the search failed. */
type Answer = { q: string; people: PersonRow[] | null };

/** Someone listed: a match, or a recent person (no detail line). */
type Listed = Pick<PersonRow, "kind" | "id" | "first_name" | "last_name"> &
  Partial<PersonRow>;

const PEOPLE_KINDS = Object.values(PersonKind);

/**
 * Admins' people search in the navbar (also in its folded menu; the navbar
 * shows it only to signed-in admins inside the app): Enter opens
 * the highlighted person, and "/" focuses it where the navbar is wide.
 * Focused with nothing typed, it lists the people the admin dealt with
 * lately (Recent).
 */
export default function PeopleSearch() {
  const { currently_selected_organization } = useContext(
    OrgRolesAndAccountContext
  );
  const wide = useMediaQuery("(min-width: 1093px)");
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [input, setInput] = useState("");
  // The newest answer stays shown while the next search loads.
  const [answer, setAnswer] = useState<Answer>();
  const [highlight, setHighlight] = useState(0);
  // Enter pressed before the answer for what is typed arrived: open the top
  // match when it does.
  const openOnAnswer = useRef(false);
  // Asked for each time the search takes the focus, so it is current.
  const [focused, setFocused] = useState(false);
  const recent = useRecentPeople(focused ? PEOPLE_KINDS : []);
  const recentRows: Listed[] = recent.slice(0, RECENT_SHOWN).map((person) => ({
    kind: person.kind as string as PersonKind,
    id: person.id as number,
    first_name: person.first_name,
    last_name: person.last_name,
  }));
  const showingRecent = !input.trim() && recentRows.length > 0;
  const people: Listed[] | null | undefined = showingRecent
    ? recentRows
    : answer?.people;

  const open = useCallback(
    (person: Listed) => {
      navigate(profilePath(person));
      setInput("");
      inputRef.current?.blur();
    },
    [navigate]
  );

  // "/" focuses the search unless the person is typing somewhere.
  useEffect(() => {
    if (!wide) return;
    function focusOnSlash(e: globalThis.KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (
        e.key !== "/" ||
        e.metaKey ||
        e.ctrlKey ||
        e.altKey ||
        target.isContentEditable ||
        ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)
      ) {
        return;
      }
      e.preventDefault();
      inputRef.current?.focus();
    }
    document.addEventListener("keydown", focusOnSlash);
    return () => document.removeEventListener("keydown", focusOnSlash);
  }, [wide]);

  // Searches 250 ms after the last keystroke. Only the newest answer is applied.
  useEffect(() => {
    const q = input.trim();
    if (!q) {
      setAnswer(undefined);
      setHighlight(0);
      return;
    }
    let current = true;
    const timer = setTimeout(async () => {
      try {
        const page = await PeopleService.searchPeople({
          orgId: currently_selected_organization as number,
          requestBody: { q, tab: PeopleTab.ALL, limit: 8 },
        });
        if (!current) return;
        setAnswer({ q, people: page.results });
        setHighlight(0);
        if (openOnAnswer.current && page.results.length) open(page.results[0]);
      } catch {
        if (current) setAnswer({ q, people: null });
      }
      if (current) openOnAnswer.current = false;
    }, 250);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [input, currently_selected_organization, open]);

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      openOnAnswer.current = false;
      setInput("");
      inputRef.current?.blur();
      return;
    }
    if (e.key === "Enter" && input.trim() && answer?.q !== input.trim()) {
      openOnAnswer.current = true;
      return;
    }
    if (!people?.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((i) => Math.min(i + 1, people.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      open(people[highlight]);
    }
  }

  return (
    <li className="people-search">
      <MagnifyingGlassIcon className="people-search__icon" />
      <input
        ref={inputRef}
        id="people-search"
        type="text"
        className="people-search__input"
        placeholder="Search people"
        aria-label="Search people"
        role="combobox"
        aria-expanded={!!people?.length}
        aria-controls="people-search-results"
        aria-activedescendant={
          people?.length ? `people-search-option-${highlight}` : undefined
        }
        maxLength={100}
        autoComplete="off"
        value={input}
        onChange={(e) => {
          openOnAnswer.current = false;
          setInput(e.target.value);
        }}
        onKeyDown={onKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          openOnAnswer.current = false;
          setFocused(false);
        }}
      />
      {wide && !input && <kbd className="people-search__key">/</kbd>}

      {/* Shown only while the search has focus (index.css). Mouse down keeps
          the focus, so a click lands before the list hides. */}
      {people !== undefined && (
        <div
          className="people-search__results"
          onMouseDown={(e) => e.preventDefault()}
        >
          {people === null ? (
            <p className="people-search__note">People could not be loaded.</p>
          ) : people.length === 0 ? (
            <p className="people-search__note">No one matches.</p>
          ) : (
            <ul
              id="people-search-results"
              role="listbox"
              aria-label={showingRecent ? RECENT_LABEL : undefined}
            >
              {showingRecent && (
                <li
                  className="ml-3 mt-2 mb-1 text-gray-500 text-sm"
                  aria-hidden
                >
                  {RECENT_LABEL}
                </li>
              )}
              {people.map((person, index) => {
                const detail = showingRecent
                  ? null
                  : personDetail(person as PersonRow);
                return (
                  <li
                    key={personKey(person)}
                    id={`people-search-option-${index}`}
                    role="option"
                    aria-selected={index === highlight}
                    className={`people-search__option${
                      index === highlight
                        ? " people-search__option--highlighted"
                        : ""
                    }`}
                    onMouseEnter={() => setHighlight(index)}
                    onClick={() => open(person)}
                  >
                    <div className="flex items-center gap-2">
                      <span className="people-search__name">
                        {personName(person) || "No name"}
                      </span>
                      <KindChip kind={person.kind} />
                    </div>
                    {detail && (
                      <div className="people-search__detail">{detail}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}
